import { PagePaths } from '@revelio/core';
import { Tab } from '@revelio/data-access';

import { RevelioPageId } from '../components/PageConfigProvider/types';

/**
 * The Analytics views available across all three primary entities (excludes
 * Screener which lives under Discovery, not Analytics). Order drives the
 * left-to-right order of the page-level tab strip in the new dashboard
 * layout. `serverTab` mirrors the per-view route gating in routes.tsx so
 * AnalyticsViewTabs can hide tabs the user isn't entitled to.
 */
export const ANALYTICS_VIEWS = [
  {
    name: 'Overview',
    serverTab: Tab.EntitySummary,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_OVERVIEW_V2,
      geography: RevelioPageId.GEOGRAPHY_OVERVIEW,
      role: RevelioPageId.ROLE_OVERVIEW,
    },
    paths: {
      company: PagePaths.COMPANY_SUMMARY,
      geography: PagePaths.GEOGRAPHY_SUMMARY,
      role: PagePaths.ROLE_SUMMARY,
    },
  },
  {
    name: 'Compositions',
    serverTab: Tab.Overview,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_COMPOSITION,
      geography: RevelioPageId.GEOGRAPHY_COMPOSITION,
      role: RevelioPageId.ROLE_COMPOSITION,
    },
    paths: {
      company: PagePaths.COMPANY_COMPOSITIONS,
      geography: PagePaths.GEOGRAPHY_COMPOSITIONS,
      role: PagePaths.ROLE_COMPOSITIONS,
    },
  },
  {
    name: 'Transitions',
    serverTab: Tab.Transition,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_TRANSITIONS,
      geography: RevelioPageId.GEOGRAPHY_TRANSITIONS,
      role: RevelioPageId.ROLE_TRANSITIONS,
    },
    paths: {
      company: PagePaths.COMPANY_TRANSITIONS,
      geography: PagePaths.GEOGRAPHY_TRANSITIONS,
      role: PagePaths.ROLE_TRANSITIONS,
    },
  },
  {
    name: 'Job Postings',
    serverTab: Tab.Posting,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_POSTINGS,
      geography: RevelioPageId.GEOGRAPHY_POSTINGS,
      role: RevelioPageId.ROLE_POSTINGS,
    },
    paths: {
      company: PagePaths.COMPANY_POSTINGS,
      geography: PagePaths.GEOGRAPHY_POSTINGS,
      role: PagePaths.ROLE_POSTINGS,
    },
  },
  {
    name: 'Pay',
    serverTab: Tab.Compensation,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_PAY,
      geography: RevelioPageId.GEOGRAPHY_PAY,
      role: RevelioPageId.ROLE_PAY,
    },
    paths: {
      company: PagePaths.COMPANY_PAY,
      geography: PagePaths.GEOGRAPHY_PAY,
      role: PagePaths.ROLE_PAY,
    },
  },
  {
    name: 'Sentiment',
    serverTab: Tab.Sentiment,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_SENTIMENT,
      geography: RevelioPageId.GEOGRAPHY_SENTIMENT,
      role: RevelioPageId.ROLE_SENTIMENT,
    },
    paths: {
      company: PagePaths.COMPANY_SENTIMENT,
      geography: PagePaths.GEOGRAPHY_SENTIMENT,
      role: PagePaths.ROLE_SENTIMENT,
    },
  },
] as const;

export const ANALYTICS_SERVER_TABS: Tab[] = ANALYTICS_VIEWS.map(
  (v) => v.serverTab
);

export type AnalyticsEntity = 'company' | 'geography' | 'role';

const ANALYTICS_REVELIO_PAGE_IDS = new Set<RevelioPageId>(
  ANALYTICS_VIEWS.flatMap((view) => Object.values(view.revelioPageIds))
);

const REVELIO_PAGE_ID_TO_ANALYTICS_ENTITY: ReadonlyMap<
  RevelioPageId,
  AnalyticsEntity
> = new Map(
  ANALYTICS_VIEWS.flatMap((view) => {
    const entries: [RevelioPageId, AnalyticsEntity][] = [
      [view.revelioPageIds.company, 'company'],
      [view.revelioPageIds.geography, 'geography'],
      [view.revelioPageIds.role, 'role'],
    ];
    return entries;
  })
);

export const isAnalyticsRevelioPage = (
  revelioPageId: RevelioPageId | undefined
): revelioPageId is RevelioPageId =>
  revelioPageId !== undefined && ANALYTICS_REVELIO_PAGE_IDS.has(revelioPageId);

export const getAnalyticsEntityFromRevelioPageId = (
  revelioPageId: RevelioPageId | undefined
): AnalyticsEntity | undefined =>
  revelioPageId
    ? REVELIO_PAGE_ID_TO_ANALYTICS_ENTITY.get(revelioPageId)
    : undefined;
